import { ICapturePaypalOrder, ICart, ICheckout, ICheckoutPage, ICreatePaypalOrder, IShippingOption, IUserCheckout } from '@interfaces';
import parse from 'html-react-parser';
import debounce from 'lodash.debounce';

import { apiGet, apiPost } from '@utils/http';
import { mapBoxes, mapUserAddresses } from './subscription.service';

export interface ICartTotalErrors {
    error: {
        data: unknown[];
        notice: string;
    }[];
}

export interface ITotalsResponse {
    cartBoxes: any;
    discount: number;
    shipping: number;
    total: number;
    shippingOptions: { [key: string]: IShippingOption };
    newDiscountResult: any;
    newDiscountCode: any;
    coupons: any;
    messages: any;
    subscription?: {
        isActive: boolean,
    }
    errors: unknown | ICartTotalErrors;
}

export interface IGiftDiscountResponse {
    discountCoefficient: number | null;
    giftBannerDescription: string | null;
    giftBannerTitle: string | null;
}

export default class CheckoutService {

    static predictDeliveryDate(data: any): Promise<any> {
        return apiGet(`aftership/predict-delivery-date?${new URLSearchParams(data).toString()}`, true)
            .then((response: any) => response as ITotalsResponse);
    }

    static calculateTotals(data: any, signal?: AbortSignal): Promise<ITotalsResponse> {
        return apiPost('checkout/cart-totals', data, true, signal)
                .then((response: any) => response as ITotalsResponse);
    }

    private static _debouncedCalculateTotals = debounce(
        (data: any, signal: AbortSignal | undefined, resolve: (value: ITotalsResponse | PromiseLike<ITotalsResponse>) => void, reject: (reason?: any) => void) => {
            CheckoutService.calculateTotals(data, signal)
                .then(resolve)
                .catch(reject);
        },
        1000
    );

    static debouncedCalculateTotals(data: any, signal?: AbortSignal): Promise<ITotalsResponse> {
        return new Promise((resolve, reject) => {
            this._debouncedCalculateTotals(data, signal, resolve, reject);
        });
    }

    /**
     * Can't be used currently
     *
     * @param data
     * @returns
     */
    static addCoupon(data: any): Promise<ITotalsResponse> {
        return apiPost('checkout/add-coupon', data)
                .then((response: any) => response as ITotalsResponse);
    }

    /**
     * Can't be used currently
     *
     * @param data
     * @returns
     */
    static removeCoupon(data: any): Promise<ITotalsResponse> {
        return apiPost('checkout/remove-coupon', data)
                .then((response: any) => response as ITotalsResponse);
    }


    static calculateCartTotals(
        user: IUserCheckout,
        checkout: ICheckout,
        cart: ICart,
        cartForm?: any,
        newDiscountCode?: string,
        removeDiscountCode?: string,
        noShipping?: boolean,
        signal?: AbortSignal,
        data?: any
    ): Promise<ITotalsResponse> {
        const boxes = mapBoxes(cart);
        const [shipping, billing] = mapUserAddresses(user);
        const overwrite = data || {};

        return CheckoutService.debouncedCalculateTotals({
            shipping: shipping,
            billing: billing,
            new_discount_code: (newDiscountCode && !removeDiscountCode) ? newDiscountCode : null,
            remove_discount_code: removeDiscountCode ? removeDiscountCode : null,
            boxes: boxes,
            discount_codes: checkout.coupons,
            shipping_method: overwrite.shippingMethod || checkout.shippingMethod || '',
            no_shipping: !!noShipping,
            order_type: cartForm ? cartForm.orderType : null
        }, signal)
            .then(response => {
                let data: any = response;

                data.newDiscountCode = (newDiscountCode && !removeDiscountCode) ? newDiscountCode : null;
                data.cartBoxesErrors = response.cartBoxes && response.cartBoxes.length > 0
                    ? response.cartBoxes.reduce((acc: any, cur: any) => acc.concat(cur.errors || []), [])
                    : [];

                return data;
            })
            .catch(e => {
                if (e.name === 'AbortError') {
                } else {
                    console.error(e);
                }
            });
    }

    private static errorHandler = (responseData: any) => {
        let errorType = null;
        let errorMessage = null;

        if (responseData.code === 'password_error') {
            errorType = "password";
            errorMessage = parse(responseData.message);
        } else if (['email_error', 'existing_user_login', 'existing_user_email'].includes(responseData.code)) {
            errorType = "email";
            errorMessage = parse(responseData.message);
        } else if (['phone_error'].includes(responseData.code)) {
            errorType = "phone";
            errorMessage = parse(responseData.message);
        } else if (['checkout-error', 'payment_error'].includes(responseData.code)) {
            errorType = "serverError";
            errorMessage = parse(responseData.message);
        } else if (!responseData.payment_success && responseData.stripe_response && responseData.stripe_response.errors) {
            errorType = "stripeError";
            switch(responseData.stripe_response.errors.stripe_error_message?.[0]) {
                case 'insufficient_funds':
                    errorMessage = `It looks like there are not enough funds in your account to complete this purchase. Please check your balance and try again.`
                    break;
                case 'expired_card':
                    errorMessage = `It looks like your card is expired. Please check your card details and try again.`
                    break;
                case 'incorrect_cvc':
                    errorMessage = `It looks like the CVC code you entered is incorrect. Please double-check the numbers on the back of your card and try again.`
                    break;
                case 'incorrect_number':
                    errorMessage = `It looks like the card number you entered is incorrect. Please check your card details and try again.`
                    break;
                default:
                    errorMessage = `Your card was declined. Please check your card details and try again.`

            }
        }

        return [errorType, errorMessage]
    }

    static checkout(data: ICheckoutPage): Promise<{ success: boolean, data?: any, error?: any}> {
        const { user, cart, stripe, checkout } = data;
        const { smsEnabled, smsNewsEnabled, email, password, phone } = user;
        const { shippingMethod, newDiscountCode, coupons } = checkout;

        const boxes = mapBoxes(cart);
        const [shipping, billing] = mapUserAddresses(user);

        const payload = {
            "stripe_source": stripe.source,
            "stripe_response": stripe.response ? stripe.response : null,
            "shipping": shipping,
            "billing": {...billing, phone},
            "sms_enabled": smsEnabled,
            "sms_news_enabled": smsNewsEnabled,
            "boxes": boxes as any,
            "user_info": {
                "email": email,
                "password_1": password,
            },
            "shipping_method": shippingMethod,
            "new_discount_code": newDiscountCode ? newDiscountCode : '',
            "discount_codes": coupons,
        };

        return apiPost('checkout/subscription', payload, null, null, true)
            .then((responseData: any) => {
                const [errorType, errorMessage] = this.errorHandler(responseData);
                const success = responseData.payment_success && responseData.order_id;

                return {
                    success: success,
                    data: responseData,
                    error: {
                        type: errorType,
                        message: errorMessage,
                    }
                };
            })
            .catch(() => {
                return {
                    success: false,
                    error: {
                        type: "serverError",
                        message: "Unable to process request, please try again."
                    }
                };
            });
    }

    static createPaypalOrder(data: ICreatePaypalOrder): Promise<{ success: boolean, data?: any, error?: any}> {
        const { user, cart, checkout } = data;
        const { smsEnabled, smsNewsEnabled, phone } = user;
        const { shippingMethod, newDiscountCode, coupons } = checkout;

        let payload;
        const boxes = mapBoxes(cart);
        if(user.password) {
            const [shipping, billing] = mapUserAddresses(user);
            payload = {
                "shipping": shipping,
                "billing": {...billing, phone},
                "sms_enabled": smsEnabled,
                "sms_news_enabled": smsNewsEnabled,
                "boxes": boxes as any,
                "user_info": {
                    "email": user.email,
                    "password_1": user.password,
                },
                "shipping_method": shippingMethod,
                "new_discount_code": newDiscountCode ? newDiscountCode : '',
                "discount_codes": coupons,
            };
        } else {
            payload = {
                // "shipping": shipping,
                // "billing": {...billing, phone},
                "sms_enabled": smsEnabled,
                "sms_news_enabled": smsNewsEnabled,
                "boxes": boxes as any,
                "shipping_method": shippingMethod,
                "new_discount_code": newDiscountCode ? newDiscountCode : '',
                "discount_codes": coupons,
            }
        }

        return apiPost('checkout/create-order-paypal', payload, null, null, true)
            .then((responseData: any) => {
                const success = !!responseData?.id;
                const [errorType, errorMessage] = this.errorHandler(responseData);
                return {
                    success: success,
                    data: responseData,
                    error: {
                        type: errorType,
                        message: errorMessage,
                    }
                };
            }).catch(() => {
                return {
                    success: false,
                    error: {
                        type: "serverError",
                        message: "Unable to process request, please try again."
                    }
                };
            });
    }

    static paypalPay(data: ICapturePaypalOrder): Promise<{ success: boolean, data?: any, error?: any}> {
        const { user, cart, checkout, orderID } = data;
        const { smsEnabled, smsNewsEnabled, phone } = user;
        const { shippingMethod, newDiscountCode, coupons } = checkout;

        let payload;
        const boxes = mapBoxes(cart);
        if(user.password) {
            const [shipping, billing] = mapUserAddresses(user);
            payload = {
                "order_id": orderID,
                "shipping": shipping,
                "billing": {...billing, phone},
                "sms_enabled": smsEnabled,
                "sms_news_enabled": smsNewsEnabled,
                "boxes": boxes as any,
                "user_info": {
                    "email": user.email,
                    "password_1": user.password,
                },
                "shipping_method": shippingMethod,
                "new_discount_code": newDiscountCode ? newDiscountCode : '',
                "discount_codes": coupons,
            };
        } else {
            payload = {
                "order_id": orderID,
                // "shipping": shipping,
                // "billing": {...billing, phone},
                "sms_enabled": smsEnabled,
                "sms_news_enabled": smsNewsEnabled,
                "boxes": boxes as any,
                "shipping_method": shippingMethod,
                "new_discount_code": newDiscountCode ? newDiscountCode : '',
                "discount_codes": coupons,
            }
        }

        return apiPost('checkout/paypal-payment', payload, null, null, true).then((responseData: any) => {
            const [errorType, errorMessage] = this.errorHandler(responseData);
            const success = user.password ? responseData.payment_success : responseData.paymentSuccess;
            return {
                success: success,
                data: responseData,
                error: {
                    type: errorType,
                    message: errorMessage,
                }
            };
        }).catch(() => {
            return {
                success: false,
                error: {
                    type: "serverError",
                    message: "Unable to process request, please try again."
                }
            };
        });
    }

    static getGiftDiscount(): Promise<any> {
        return apiGet(`checkout/gift-discount`, true)
            .then((response: any) => response as ITotalsResponse);
    }
}